/**
 * The two requests and the cache refresh behind the "Assign installation to
 * imports" step (backend docs/features/import-installation-backfill), shared by
 * the add-emissions dialog and the template upload. `http` is passed in so a
 * caller's client override still applies.
 */
import { initialSelection } from './assignImports';

/** The supplier-only imports a factor (saved or about to be) covers. */
export const previewAssignableImports = (http, { installationId, cnGood, date, lastRecordDate }) =>
  http
    .post('/emissions/assignable-imports', { installationId, cnGood, date, lastRecordDate })
    .then((res) => res?.data ?? null);

/** Lines per assign request: a few hundred in one request ran longer than a request may. */
export const ASSIGN_BATCH_SIZE = 100;

/**
 * Assign the saved factor's installation to the chosen imports, in batches of
 * ASSIGN_BATCH_SIZE (one after another: a shipment's lines can fall in two batches).
 * `assigned` counts the imports now priced on an installation factor; `kept` the ones that got
 * the installation but keep their factor because the installation has several products for the
 * CN good (backend D19).
 */
export const assignImports = async (http, factorId, lines) => {
  const totals = { assigned: 0, kept: 0, skipped: 0 };
  for (let start = 0; start < lines.length; start += ASSIGN_BATCH_SIZE) {
    const res = await http.post(`/emissions/${factorId}/assign-imports`, {
      lines: lines.slice(start, start + ASSIGN_BATCH_SIZE),
    });
    const kept = res?.data?.keptFactor?.length ?? 0;
    totals.assigned += (res?.data?.assigned ?? 0) - kept;
    totals.kept += kept;
    totals.skipped += res?.data?.skipped?.length ?? 0;
  }
  return totals;
};

/** The derived-query refresh after a write does not cover the imports lists. */
export const invalidateImportQueries = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['shipmentsSearch'] });
  queryClient.invalidateQueries({ queryKey: ['shipments'] });
};

/**
 * The step sections for factors a template upload just created: one per factor that
 * covers supplier-only imports. A failed lookup only means no section for that factor.
 */
export const findAssignSections = async (http, factors) => {
  const sections = await Promise.all(
    (factors ?? []).map(async (factor) => {
      if (!factor?._id || !factor.installationId || !factor.date) return null;
      try {
        const preview = await previewAssignableImports(http, {
          ...factor,
          lastRecordDate: factor.lastRecordDate ?? factor.date,
        });
        return preview?.lines?.length > 0
          ? { factor, preview, selected: initialSelection(preview.lines) }
          : null;
      } catch (err) {
        // Logged so a missing step is traceable; the upload itself succeeded.
        console.warn('Assignable-imports preview failed', err);
        return null;
      }
    }),
  );
  return sections.filter(Boolean);
};
